import { getApi, postApi } from "../../../helpers/auth_helpers";

const base = () => String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

export type ApiResult<T> = { success?: boolean; data?: T; message?: string; code?: string; status?: number };

export type ShadePhoto = {
  id: string;
  state: "pending" | "approved" | "rejected";
  isPrimary: boolean;
  shade?: string | null;
  takenAt?: string | null;
  uploadedBy?: string | null;
  promotedToMainAt?: string | null;
  ready: boolean;
  thumbnail?: string | null;
  card?: string | null;
  detail?: string | null;
  zoom?: string | null;
};

export type ShadeLinkRow = { id: string; expiresAt: string; used: number; maxUploads: number; createdBy?: string | null };

export type ShadeList = {
  pending: ShadePhoto[];
  approved: ShadePhoto[];
  rejected: ShadePhoto[];
  canReview: boolean;
  links?: ShadeLinkRow[];
  addedToMain?: boolean;
};

export type ShadeLink = { linkId: string; token: string; path: string; url?: string | null; expiresAt: string; maxUploads: number };

const productUrl = (mongoId: string) => `${base()}/studio/products/${encodeURIComponent(mongoId)}`;
const photoUrl = (mongoId: string, photoId: string, action: string) =>
  `${productUrl(mongoId)}/shade-photos/${encodeURIComponent(photoId)}/${action}`;

export const fetchShadePhotos = (mongoId: string): Promise<ApiResult<ShadeList>> => getApi(`${productUrl(mongoId)}/shade-photos`);
export const approveShadePhoto = (mongoId: string, photoId: string): Promise<ApiResult<ShadeList>> => postApi(photoUrl(mongoId, photoId, "approve"), {});
export const rejectShadePhoto = (mongoId: string, photoId: string): Promise<ApiResult<ShadeList>> => postApi(photoUrl(mongoId, photoId, "reject"), {});
export const useShadeAsMain = (mongoId: string, photoId: string): Promise<ApiResult<ShadeList>> => postApi(photoUrl(mongoId, photoId, "use-as-main"), {});
export const createShadeLink = (mongoId: string): Promise<ApiResult<ShadeLink>> => postApi(`${productUrl(mongoId)}/shade-links`, {});
export const revokeShadeLink = (mongoId: string, linkId: string): Promise<ApiResult<ShadeList>> =>
  postApi(`${productUrl(mongoId)}/shade-links/${encodeURIComponent(linkId)}/revoke`, {});

/** The full link a worker opens: the server's URL when it knows the app host, else this app's own origin. */
export const shareableLink = (link: ShadeLink): string =>
  link.url || `${typeof window !== "undefined" ? window.location.origin : ""}${link.path}`;

// ── Worker page (no login: the token is the only key, sent in the body) ──

export type ShadeView = {
  productName?: string | null;
  serial?: string | null;
  colour?: string | null;
  dyed: boolean;
  coverUrl?: string | null;
  used: number;
  maxUploads: number;
  remaining: number;
  expiresAt: string;
};

export type UploadResult = { ok: boolean; duplicate: boolean; used: number; maxUploads: number; remaining: number };

/** Codes that close the whole page, and codes that refuse one photo. */
export const CLOSED_CODES = ["EXPIRED", "REVOKED", "LINK_FULL", "NOT_VALID"] as const;
export const PHOTO_CODES = ["UNSUPPORTED_TYPE", "TOO_LARGE"] as const;

export const fetchShadeView = async (token: string): Promise<ApiResult<ShadeView>> => {
  try {
    const res = await fetch(`${base()}/public/shade-links/view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    const json = await res.json().catch(() => ({}));
    return { ...json, status: res.status };
  } catch {
    return { success: false, code: "OFFLINE", status: 0 };
  }
};

/**
 * Sends one photo with upload progress. Resolves with the server's answer; status 0 means the network failed
 * (keep the photo and try again later).
 */
export const uploadShadePhoto = (
  token: string,
  blob: Blob,
  clientUploadId: string,
  onProgress: (percent: number) => void
): Promise<ApiResult<UploadResult>> =>
  new Promise((resolve) => {
    const form = new FormData();
    form.append("token", token);
    form.append("clientUploadId", clientUploadId);
    form.append("file", blob, "shade.jpg");
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${base()}/public/shade-links/photos`);
    xhr.timeout = 120000;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((100 * event.loaded) / event.total));
    };
    xhr.onload = () => {
      let json: any = {};
      try {
        json = JSON.parse(xhr.responseText || "{}");
      } catch {
        json = {};
      }
      resolve({ ...json, status: xhr.status });
    };
    xhr.onerror = xhr.ontimeout = () => resolve({ success: false, code: "OFFLINE", status: 0 });
    xhr.send(form);
  });
